import { RepeatIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  HStack,
  IconButton,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { useCallback, useMemo, useRef, useState } from 'react';

import type { TalentDiscoveryRespUserV3 } from '@revelio/data-access';

import type { FullProfileInfoData } from '../CustomDash/components/Chart/FullProfileInfo/types';
import { Logo } from '../routes/side-nav/Logo';
import { CurrencySelect } from './CurrencySelect';
import { useSalaryControlsStore } from './hooks/useSalaryControlsStore';
import {
  CompInfo,
  EmailRow,
  ExperienceRow,
  LocationRow,
  RoleRow,
} from './ProfileCard';
import { ResultsView } from './ResultsView';
import { SplashView } from './SplashView';
import './individual-potential.css';

import { getEffectiveSalary } from './utils/getEffectiveSalary';

// Re-export types so existing consumers don't need to update imports
export type { SalaryToggles, UpliftMode } from './hooks/useSalaryControlsStore';

export const IndividualPotentialPage = () => {
  const [phase, setPhase] = useState<'splash' | 'results'>('splash');
  const [searchName, setSearchName] = useState('');
  const [selectedUser, setSelectedUser] =
    useState<TalentDiscoveryRespUserV3 | null>(null);
  const [profileData, setProfileData] = useState<FullProfileInfoData | null>(
    null
  );

  // Shared salary controls (store eliminates prop drilling to ResultsView)
  const simulatedSalary = useSalaryControlsStore((s) => s.simulatedSalary);
  const setSimulatedSalary = useSalaryControlsStore(
    (s) => s.setSimulatedSalary
  );
  const upliftMode = useSalaryControlsStore((s) => s.upliftMode);
  const setUpliftMode = useSalaryControlsStore((s) => s.setUpliftMode);
  const userPercentile = useSalaryControlsStore((s) => s.userPercentile);
  const resetSalaryControls = useSalaryControlsStore((s) => s.reset);

  const effective = useMemo(() => {
    if (!profileData) return null;
    return getEffectiveSalary(profileData, simulatedSalary);
  }, [profileData, simulatedSalary]);

  const handleTakeOff = (name: string) => {
    setSearchName(name);
    setPhase('results');
  };

  const handleProfileLoaded = (
    user: TalentDiscoveryRespUserV3,
    profile: FullProfileInfoData
  ) => {
    setSelectedUser(user);
    setProfileData(profile);
  };

  /** Bump to force unmount/remount of ResultsView with the same user */
  const [refreshKey, setRefreshKey] = useState(0);

  const handleRefresh = useCallback(() => {
    setSelectedUser(null);
    setProfileData(null);
    resetSalaryControls();
    setRefreshKey((k) => k + 1);
  }, [resetSalaryControls]);

  const containerRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (el) {
      setIsScrolled(el.scrollTop > 0);
    }
  }, []);

  return (
    <div
      className="potential-container"
      style={{ position: 'relative' }}
      ref={containerRef}
      onScroll={handleScroll}
    >
      {phase === 'splash' && (
        <Flex
          position="absolute"
          top={4}
          right={6}
          zIndex={10}
          align="center"
          gap={1}
          bg="white"
          borderRadius="full"
          px={3}
          py={1}
          boxShadow="0 1px 4px rgba(45, 66, 106, 0.06)"
          border="1px solid"
          borderColor="silver.400"
        >
          <CurrencySelect inline />
          <IconButton
            aria-label="Re-run with same user"
            icon={<RepeatIcon />}
            size="sm"
            variant="ghost"
            color="gray.500"
            _hover={{ color: 'gray.700', bg: 'gray.100' }}
            onClick={handleRefresh}
          />
        </Flex>
      )}

      <div
        className={`potential-header ${
          phase === 'splash'
            ? 'potential-header--splash'
            : 'potential-header--results'
        }`}
      >
        <SplashView phase={phase} onTakeOff={handleTakeOff} />
      </div>

      {phase === 'results' && (
        <>
          <div
            className={`potential-top-bar-wrapper${
              isScrolled ? ' potential-top-bar-wrapper--scrolled' : ''
            }`}
          >
            <div
              className={`potential-top-bar ${
                profileData
                  ? 'potential-top-bar--visible'
                  : 'potential-top-bar--hidden'
              }`}
            >
              {/* Row 1, Col 1: Logo + Potential + Name */}
              <HStack spacing={2}>
                <Box
                  w="18px"
                  h="18px"
                  flexShrink={0}
                  sx={{ '& svg': { width: '100%', height: '100%' } }}
                >
                  <Logo />
                </Box>
                <Text
                  fontSize="md"
                  fontWeight="bold"
                  bgGradient="linear(to-r, navyBlue.500, green.500)"
                  bgClip="text"
                  whiteSpace="nowrap"
                >
                  Potential
                </Text>
                <Text
                  fontSize="lg"
                  fontWeight="semibold"
                  color="navyBlue.700"
                  whiteSpace="nowrap"
                >
                  {searchName}
                </Text>
              </HStack>

              {/* Row 1, Col 2: spacer */}
              <Box />

              {/* Row 1, Col 3: Role + LinkedIn */}
              <RoleRow profileData={profileData} isLoading={!profileData} />

              {/* Row 1, Col 4: Location */}
              <LocationRow profileData={profileData} isLoading={!profileData} />

              {/* Row 1, Col 5: Currency + Refresh */}
              <HStack spacing={2}>
                <CurrencySelect inline />
                <IconButton
                  aria-label="Re-run with same user"
                  icon={<RepeatIcon />}
                  size="sm"
                  variant="ghost"
                  color="gray.500"
                  _hover={{ color: 'gray.700', bg: 'gray.100' }}
                  onClick={handleRefresh}
                />
              </HStack>

              {/* Row 2, Col 1: Comp info + Uplift mode toggle */}
              <HStack spacing={3}>
                <CompInfo
                  profileData={profileData}
                  isLoading={!profileData}
                  simulatedSalary={simulatedSalary}
                  onSimulatedSalaryChange={setSimulatedSalary}
                />
                <ButtonGroup size="sm" isAttached variant="outline">
                  <Button
                    onClick={() => setUpliftMode('median')}
                    colorScheme={upliftMode === 'median' ? 'navyBlue' : 'gray'}
                    variant={upliftMode === 'median' ? 'solid' : 'outline'}
                  >
                    Median
                  </Button>
                  <Tooltip
                    label="Compare salaries at your same percentile across skills, roles, and geographies"
                    fontSize="xs"
                  >
                    <Button
                      onClick={() => setUpliftMode('percentile')}
                      colorScheme={
                        upliftMode === 'percentile' ? 'navyBlue' : 'gray'
                      }
                      variant={
                        upliftMode === 'percentile' ? 'solid' : 'outline'
                      }
                      isDisabled={userPercentile === null}
                    >
                      At Your Percentile
                    </Button>
                  </Tooltip>
                </ButtonGroup>
              </HStack>

              {/* Row 2, Col 2: spacer */}
              <Box />

              {/* Row 2, Col 3: Experience */}
              <ExperienceRow
                yearsOfExperience={selectedUser?.years_of_experience}
                isLoading={!profileData}
              />

              {/* Row 2, Col 4: Email */}
              <EmailRow profileData={profileData} isLoading={!profileData} />

              {/* Row 2, Col 5: empty */}
              <Box />
            </div>
          </div>

          <div
            className={`potential-results ${
              profileData
                ? 'potential-results--visible'
                : 'potential-results--hidden'
            }`}
          >
            <ResultsView
              key={refreshKey}
              searchName={searchName}
              selectedUser={selectedUser}
              profileData={profileData}
              userSalary={effective?.userSalary ?? 0}
              salaryTypeLabel={effective?.salaryTypeLabel ?? 'Base'}
              isSimulatedSalary={effective?.isSimulated ?? false}
              onProfileLoaded={handleProfileLoaded}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default IndividualPotentialPage;
